import {
  ComponentProps,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  ReactPortal,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import styled, { CSSObject } from 'styled-components';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { useDeepEqualMemoValue } from '@components/component-helpers';
import { DATEPICKER_PORTAL_ID } from '@components/datepicker/datepicker';
import { HorizontalTabs } from '@components/horizontal-tabs';
import { IconButton } from '@components/icon-button';
import { BaseIcon, IconProps } from '@components/icons/base';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconHelp } from '@components/icons/help';
import { RelativeSpinner } from '@components/loading-spinner';
import { HEADLESS_PORTAL_ID } from '@components/menu';
import { useIsModalActive } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Keys } from '@components/types/keyboard';
import { useKeyPress } from '@hooks/use-keypress';
import { useWindowDimensions } from '@hooks/use-window-dimensions';
import { createCountStore } from '@stores/create-count-store';
import { datepickerPopperSelector } from '@styles/datepicker';
import { Color, scrollbarStyle, theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { MakePropertiesOptional, Transient } from '@utils/types';

export type SidebarOptions = {
  id?: string;
  extendedId?: string;
  hasBackdrop?: boolean;
  variant?: 'REGULAR' | 'LARGE' | 'CONTENT_HEAVY' | 'REGULAR_EXPANDED';
  isOverrideEnabled?: boolean;
  closeOnClickOutside?: boolean;
  onHide?(): void;
  onBeforeChange?(): boolean | void;
  containerStyle?: CSSObject;
  backgroundColor?: Color | 'gradient';
  zIndex?: number;
};

export type SidebarComponent = (props: any) => ReactElement;

type SidebarRegistry = {
  options?: SidebarOptions;
  contentPortal: ReactPortal;
  extendedContentComponent?: SidebarComponent;
};

let sidebarRegistry: SidebarRegistry | null = null;

const sidebarStore = createCountStore();
const expandedStore = createCountStore();

const handleHide = (forceHide: boolean) => {
  if (sidebarRegistry?.options) {
    const { onBeforeChange, onHide } = sidebarRegistry.options;
    sidebarRegistry.options.isOverrideEnabled = false;
    const shouldContinue = forceHide || (onBeforeChange?.() ?? true);
    if (shouldContinue) {
      sidebarRegistry = null;
      onHide?.();
      sidebarStore.reset();
      expandedStore.reset();
    }
  }
};

export const hideSidebar = () => handleHide(false);

export const forceHideSidebar = () => handleHide(true);

export const hideExtendedSidebar = () => {
  if (sidebarRegistry?.extendedContentComponent) {
    sidebarRegistry.extendedContentComponent = undefined;
    if (sidebarRegistry.options) {
      sidebarRegistry.options.extendedId = undefined;
    }
    expandedStore.reset();
  }
};

const SIDEBAR_ID = 'sidebar';
const HEADER_ID = 'sidebar-header';
const CONTENT_ID = 'sidebar-content';
const OVERRIDE_CONTENT_ID = 'sidebar-override-content';
const FOOTER_ID = 'sidebar-footer';

const hiddenStyle = { height: 0, opacity: 0, overflow: 'hidden' };

export const useSidebar = <T extends SidebarComponent>(component: T, options?: SidebarOptions) => {
  const opts = useDeepEqualMemoValue(options);
  return useCallback(
    <K extends ComponentProps<T>>(props?: K, optionsOnShow?: SidebarOptions) => {
      return showSidebar({
        component,
        options: { ...opts, ...optionsOnShow },
        props,
      });
    },
    [component, opts]
  );
};

export const useSidebarState = () => {
  const { useStore } = sidebarStore;
  const isActive = useStore((s) => s.count);
  const activeId = sidebarRegistry?.options?.id;

  return { isActive, activeId };
};

export const useIsSidebarActive = (id: string) => {
  const { activeId } = useSidebarState();

  return id === activeId;
};

export const showSidebar = <T extends SidebarComponent, K extends ComponentProps<T>>({
  component: SidebarComponent,
  options,
  props,
}: {
  component: T;
  options?: SidebarOptions;
  props?: K;
}) => {
  const sidebarElement = document.getElementById(SIDEBAR_ID);
  const shouldContinue = sidebarRegistry?.options?.onBeforeChange?.() ?? true;
  hideExtendedSidebar();
  if (!sidebarElement || !shouldContinue) {
    return;
  }
  // To re-mount the sidebar when it is reopened, so we don't re-use old state
  const ts = Date.now().valueOf();
  // @ts-ignore-next-line Little Klemenz doesn't know how to fix
  const contentPortal = createPortal(<SidebarComponent key={ts} {...props} />, sidebarElement);
  sidebarRegistry = { options: { ...options, ...props }, contentPortal };
  sidebarStore.increase();
};

export const useSidebarEventHandlers = () => {
  const setOnBeforeChange = (onBeforeChange: SidebarOptions['onBeforeChange']) => {
    if (sidebarRegistry?.options) {
      sidebarRegistry.options = { ...sidebarRegistry.options, onBeforeChange };
    }
  };

  const setOnHide = (onHide: SidebarOptions['onHide']) => {
    if (sidebarRegistry?.options) {
      sidebarRegistry.options = { ...sidebarRegistry.options, onHide };
    }
  };

  return { setOnBeforeChange, setOnHide };
};

const TRANSITION_DURATION = 0.3;
const BACKDROP_OPACITY = 0.3;

const LARGE_BREAKPOINT = 768;
const LARGE_MAX_WIDTH = 696;
const REGULAR_MAX_WIDTH = 472;

const useSidebarWidth = (variant: SidebarOptions['variant']) => {
  const { width } = useWindowDimensions();
  const dynamicWidth =
    variant !== 'REGULAR'
      ? width > LARGE_BREAKPOINT
        ? LARGE_MAX_WIDTH
        : REGULAR_MAX_WIDTH
      : REGULAR_MAX_WIDTH;
  const validWidth = Math.min(width, dynamicWidth);
  return pxToRem(validWidth);
};

export const Sidebar = (props: any) => <div {...props} />;

export const setSidebarVariant = (variant: SidebarOptions['variant']) => {
  if (sidebarRegistry?.options) {
    sidebarRegistry.options.variant = variant;
    sidebarStore.increase();
  }
};

const VariantContext = createContext<SidebarOptions['variant']>('REGULAR');
const OverrideContext = createContext<{
  isEnabled: Boolean;
  setIsEnabled: (isEnabled: boolean) => void;
}>({ isEnabled: false, setIsEnabled: noop });

export const useSidebarOverrideContext = () => {
  const overrideContext = useContext(OverrideContext);

  return overrideContext;
};

type ShowExpandedSidebarProps = {
  component: SidebarComponent;
  extendedId?: SidebarOptions['extendedId'];
};

export const showExtendedSidebar = ({ component, extendedId }: ShowExpandedSidebarProps) => {
  if (sidebarRegistry) {
    sidebarRegistry = {
      ...sidebarRegistry,
      extendedContentComponent: component,
      options: { ...sidebarRegistry.options, extendedId },
    };
    expandedStore.increase();
  }
};

export const useExtendedSidebarData = () => {
  const isExtended = Boolean(expandedStore.useStore((s) => s.count));
  const extendedId = sidebarRegistry?.options?.extendedId;
  return { isExtended, extendedId };
};

const SidebarProvider = () => {
  const isActive = sidebarStore.useStore((s) => s.count);
  const { isExtended } = useExtendedSidebarData();
  const isModalActive = useIsModalActive();
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { options, contentPortal } = sidebarRegistry ?? {};

  const ExtendedContent = sidebarRegistry?.extendedContentComponent;
  const {
    hasBackdrop,
    variant,
    closeOnClickOutside = false,
    isOverrideEnabled = false,
    backgroundColor,
    zIndex,
  } = options ?? {};
  const baseZIndex = zIndex ?? 9990;
  const setIsOverrideEnabled = (isEnabled: boolean) => {
    if (sidebarRegistry?.options) {
      sidebarRegistry.options.isOverrideEnabled = isEnabled;
      sidebarStore.increase();
    }
  };

  useKeyPress({
    key: Keys.Escape,
    enabled: !isModalActive,
    handler: hideSidebar,
  });

  const width = useSidebarWidth(variant);

  useEffect(() => {
    forceHideSidebar();
  }, [location]);

  useEffect(() => {
    if (!closeOnClickOutside || !isActive) return;

    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;

      const isClickInside = (selectors: string[]) =>
        selectors.some((selector) => clickedElement.closest(selector));

      // Allow clicks inside popups like Menu or DatePicker, as they are rendered outside the sidebar and would otherwise close it.
      const allowedTargets = [`#${HEADLESS_PORTAL_ID}`, `#${DATEPICKER_PORTAL_ID}`];

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(clickedElement) &&
        !isClickInside(allowedTargets)
      ) {
        hideSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, closeOnClickOutside]);

  const overrideHideStyle = isOverrideEnabled ? hiddenStyle : {};

  return (
    <StyledSidebar
      id={SIDEBAR_ID}
      ref={sidebarRef}
      // @ts-ignore-next-line cannot wrap my head arount why $backgroundColor is not working
      $backgroundColor={backgroundColor}
      $zIndex={baseZIndex}
      className={backgroundColor === 'gradient' ? 'dark' : undefined}
    >
      <OverrideContext.Provider
        value={{ isEnabled: isOverrideEnabled, setIsEnabled: setIsOverrideEnabled }}
      >
        {contentPortal}
        <AnimatePresence>
          {isActive && hasBackdrop && (
            <Backdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: BACKDROP_OPACITY }}
              transition={{ duration: TRANSITION_DURATION }}
              exit={{ opacity: 0 }}
              onClick={hideSidebar}
            />
          )}
        </AnimatePresence>
        <AnimationContainer
          initial={false}
          transition={{ stiffness: 100, duration: TRANSITION_DURATION }}
          animate={{ width: isActive ? width : 0 }}
          $zIndex={baseZIndex}
        >
          <ExtendedContentContainer $isExtended={isExtended} $sidebarWidth={width}>
            {ExtendedContent !== undefined ? <ExtendedContent /> : null}
          </ExtendedContentContainer>
          <VariantContext.Provider value={variant}>
            <Container
              $width={width}
              $containerStyle={options?.containerStyle}
              $backgroundColor={backgroundColor}
            >
              <div id={HEADER_ID} />
              <ContentContainer>
                <ContentWrapper variant={variant}>
                  <StyledDiv id={CONTENT_ID} style={overrideHideStyle} />
                  <StyledDiv id={OVERRIDE_CONTENT_ID} $hideOverride={!isOverrideEnabled} />
                </ContentWrapper>
              </ContentContainer>
              <div id={FOOTER_ID} style={overrideHideStyle} />
            </Container>
          </VariantContext.Provider>
        </AnimationContainer>
      </OverrideContext.Provider>
    </StyledSidebar>
  );
};

const StyledDiv = styled.div(({ $hideOverride }: { $hideOverride?: boolean }) => ({
  transition: 'opacity 0.3s ease-in-out',
  '&:after': {
    display: 'block',
    content: "''",
    width: '100%',
    height: $hideOverride ? 0 : pxToRem(40),
  },
}));

const StyledSidebar = styled.div<Transient<Pick<SidebarOptions, 'backgroundColor' | 'zIndex'>>>(
  // @ts-ignore !important is not good, but didn't get && to work instead :( -Klemenz
  ({ $backgroundColor, $zIndex }) => ({
    position: 'fixed',
    top: 0,
    right: 0,
    zIndex: $zIndex ?? 9990,
    height: '100vh',
    boxShadow: theme.shadow.fancy,
    [datepickerPopperSelector]: {
      position: 'fixed !important',
    },
    ...($backgroundColor && {
      '&': {
        backgroundColor: $backgroundColor !== 'gradient' ? $backgroundColor : undefined,
        ...($backgroundColor === 'gradient' && {
          backgroundColor: theme.color.purple400,
          backgroundImage: `radial-gradient(ellipse 120% 100% at 100% 50%, ${theme.color.violet100} -64%, ${theme.color.tealTrans10} 70%)`,
          color: 'white',
        }),
      },
      [`aside, .${HEADER_CONTAINER_CLASSNAME}, .${FOOTER_CONTAINER_CLASSNAME}`]: {
        backgroundColor: 'transparent',
      },
    }),
  })
);

const AnimationContainer = styled(motion.aside)<Transient<Pick<SidebarOptions, 'zIndex'>>>(
  ({ $zIndex }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    // ensure the animated container sits above the backdrop
    zIndex: ($zIndex ?? 9990) + 1,
    backgroundColor: theme.color.backgroundLightDark,
  })
);

const Container = styled.div(
  ({
    $width,
    $containerStyle,
    $backgroundColor,
  }: {
    $width: string;
    $containerStyle?: CSSObject;
    $backgroundColor: SidebarOptions['backgroundColor'];
  }) => ({
    minWidth: $width,
    display: 'flex',
    flexDirection: 'column' as 'column',
    flex: 1,
    overflow: 'hidden',
    borderLeft: `${pxToRem(1)} solid ${theme.color.border10}`,
    zIndex: 1,
    backgroundColor: !$backgroundColor ? theme.color.backgroundLightDark : 'transparent',
    ...$containerStyle,
  })
);

const Backdrop = styled(motion.div)({
  position: 'fixed',
  backgroundColor: theme.color.backgroundPrimary,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: 'hidden',
  zIndex: -1,
});

const ContentContainer = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'auto',
  minHeight: 0,
  flexGrow: 1,
  overflowX: 'hidden',
  ...scrollbarStyle,
});

const ContentWrapper = styled.div<Pick<SidebarOptions, 'variant'>>(({ variant }) => ({
  flex: 1,
  margin:
    variant === 'REGULAR' || variant === 'CONTENT_HEAVY'
      ? pxToRem(24)
      : `${pxToRem(24)} ${pxToRem(56)}`,
  marginBottom: pxToRem(48),
  maxWidth: variant === 'CONTENT_HEAVY' ? pxToRem(648) : pxToRem(424),
}));

const headerFooterPadding = `${pxToRem(16)} ${pxToRem(24)}`;

type HeaderProps = { hasTabs?: boolean };

const HEADER_CONTAINER_CLASSNAME = 'header-container';

const Header = styled.div.attrs({
  className: HEADER_CONTAINER_CLASSNAME,
})<HeaderProps>(({ hasTabs }) => ({
  padding: headerFooterPadding,
  borderBottom: hasTabs ? undefined : `${pxToRem(1)} solid`,
  borderColor: theme.color.border10,
  marginBottom: hasTabs ? `-${pxToRem(16)}` : undefined,
  backgroundColor: theme.color.backgroundLightDark,
  '@media (max-width: 768px)': {
    height: pxToRem(72),
  },
}));

const FOOTER_CONTAINER_CLASSNAME = 'footer-container';

const Footer = styled.div.attrs({
  className: FOOTER_CONTAINER_CLASSNAME,
})({
  padding: headerFooterPadding,
  borderTop: `${pxToRem(1)} solid`,
  borderColor: theme.color.border10,
  backgroundColor: theme.color.backgroundLightDark,
  '@media (max-width: 768px)': {
    height: pxToRem(72),
  },
});

const SidebarHeader: React.FC<HeaderProps> = (props) => {
  const headerElement = document.getElementById(HEADER_ID);
  if (!headerElement) {
    return null;
  }

  return createPortal(<Header {...props} />, headerElement);
};

const SidebarContent: React.FC = ({ children, ...rest }) => {
  const contentElement = document.getElementById(CONTENT_ID);
  const isReadOnly = useSidebarReadOnlyContext();
  if (!contentElement) {
    return null;
  }

  return createPortal(
    <>
      <div style={{ position: 'relative' }} {...rest}>
        {isReadOnly && <Overlay />}
        {children}
      </div>
    </>,
    contentElement
  );
};

const Overlay = styled.div({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  zIndex: 1,
  height: '100%',
  width: '100%',
});

export const SidebarReadOnlyContext = createContext(false);

const useSidebarReadOnlyContext = () => useContext(SidebarReadOnlyContext);

const SidebarOverrideContent: React.FC = (props) => {
  const overrideContentElement = document.getElementById(OVERRIDE_CONTENT_ID);
  if (!overrideContentElement) {
    return null;
  }

  return createPortal(
    <Slide key="info" offset={-30}>
      <div {...props} />
    </Slide>,
    overrideContentElement
  );
};

const SidebarFooter: React.FC = (props) => {
  const footerElement = document.getElementById(FOOTER_ID);
  if (!footerElement) {
    return null;
  }

  return createPortal(<Footer {...props} />, footerElement);
};

type HeaderTabsProps = {
  tabs: Array<{
    label: string;
    content: ReactNode;
    tabCount?: number;
  }>;
  defaultIndex?: number;
  onTabChange?: (index: number) => void;
};

const TabsWrapper = styled.div({
  margin: `auto -${pxToRem(24)}`,
});

const HeaderTabs = ({ tabs, defaultIndex = 0, onTabChange }: HeaderTabsProps) => {
  const parsedTabs = tabs.map((tab, index) => ({
    label: tab.label,
    content: <Sidebar.Content>{tab.content}</Sidebar.Content>,
    index,
    tabCount: tab.tabCount,
  }));

  const { isEnabled } = useSidebarOverrideContext();

  const overrideHideStyle = isEnabled ? hiddenStyle : {};

  return (
    <TabsWrapper style={overrideHideStyle}>
      <HorizontalTabs items={parsedTabs} defaultIndex={defaultIndex} onTabChange={onTabChange} />
    </TabsWrapper>
  );
};

type SimpleIconHeaderProps = {
  title: string;
  icon: typeof BaseIcon;
  color?: Color;
  iconColor?: Color;
};

export const SimpleIconHeader = ({
  title,
  iconColor,
  color,
  icon: Icon,
}: SimpleIconHeaderProps) => (
  <Stack space={2} orientation="horizontal" align="center">
    <Icon color={iconColor ?? color ?? 'buttonIconPositive'} size={24} />
    <HeaderTitle color={color} title={title} />
  </Stack>
);

export const InfoButton: React.FC = (props) => {
  const { isEnabled, setIsEnabled } = useSidebarOverrideContext();

  return (
    <>
      <IconButton
        variant="ghost-purple"
        icon={<IconHelp color={isEnabled ? 'elementBlue100' : undefined} />}
        onClick={() => setIsEnabled(!isEnabled)}
      />
      {isEnabled && <SidebarOverrideContent {...props} />}
    </>
  );
};

type UseInfoButtonProps = {
  renderComponent: ReactNode;
};

export const useInfoButton = ({ renderComponent }: UseInfoButtonProps) => {
  const { setIsEnabled } = useSidebarOverrideContext();

  return { InfoButton: () => <InfoButton>{renderComponent}</InfoButton>, setIsEnabled };
};

type CloseButtonProps = { onClose?(): void };

export const CloseButton = ({ onClose }: CloseButtonProps) => (
  <Button
    iconLeft={<IconCloseRegular />}
    variant="ghost"
    onClick={onClose ?? hideSidebar}
    dataCy="close-sidebar"
    dataTestid="close-sidebar"
  />
);

export type SimpleHeaderProps = CloseButtonProps &
  MakePropertiesOptional<SimpleIconHeaderProps, 'icon'> & {
    tabs?: HeaderTabsProps['tabs'];
    extraHeader?: ReactElement;
    hideExtraHeaderSeparator?: boolean;
    defaultIndex?: number;
    customIcon?: ReactElement<IconProps>;
    hideCloseButton?: boolean;
    onTabChange?: HeaderTabsProps['onTabChange'];
  };

const SimpleHeader: FC<SimpleHeaderProps> = ({
  title,
  icon,
  tabs,
  extraHeader,
  hideExtraHeaderSeparator = false,
  children,
  onClose,
  defaultIndex,
  customIcon,
  hideCloseButton = false,
  onTabChange,
  ...rest
}) => (
  <SidebarHeader hasTabs={Boolean(tabs)} {...rest}>
    <Stack space={4}>
      <Stack space={2} orientation="horizontal" align="center" justify="space-between">
        {customIcon ? (
          <Stack space={2} orientation="horizontal" align="center">
            {customIcon}
            <HeaderTitle title={title} />
          </Stack>
        ) : icon ? (
          <SimpleIconHeader icon={icon} title={title} {...rest} />
        ) : (
          <HeaderTitle title={title} />
        )}
        <Stack space={2} orientation="horizontal" align="center">
          {children}
          {!hideCloseButton && <CloseButton onClose={onClose} />}
        </Stack>
      </Stack>
      {extraHeader && (
        <>
          {!hideExtraHeaderSeparator && <ContentSeparator />}
          {extraHeader}
        </>
      )}
      {tabs && <HeaderTabs tabs={tabs} defaultIndex={defaultIndex} onTabChange={onTabChange} />}
    </Stack>
  </SidebarHeader>
);

const HeaderTitle = ({ title, color }: Pick<SimpleHeaderProps, 'title' | 'color'>) => (
  <Text color={color} text={title} font="bold18" />
);

const SeparatorContainer = styled.div({
  overflow: 'hidden',
  marginLeft: -LARGE_MAX_WIDTH,
  marginRight: -LARGE_MAX_WIDTH,
});

const Separator = styled.div({
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
});

const SpinnerContainer = styled.div({
  position: 'absolute',
  left: 0,
  top: '30%',
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ContentSeparator = () => (
  <SeparatorContainer>
    <Separator />
  </SeparatorContainer>
);

const SidebarSpinner = () => {
  return (
    <SpinnerContainer>
      <RelativeSpinner />
    </SpinnerContainer>
  );
};

const StyledSplitContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  '>div:nth-child(2)': {
    width: pxToRem(424),
  },
});

type SidebarSplitContainerProps = {
  label: string;
};

const SidebarSplitContainer: React.FC<SidebarSplitContainerProps> = ({ label, children }) => {
  return (
    <StyledSplitContainer>
      <Text font="semibold14" text={label} />
      {children}
    </StyledSplitContainer>
  );
};

type ExtendedPanelProps = {
  sidebarWidth: string;
};

type ExtendedContentContainerProps = Transient<ExtendedPanelProps, 'sidebarWidth'> & {
  $isExtended: boolean;
};

const ExtendedContentContainer = styled.div<ExtendedContentContainerProps>(
  ({ $isExtended, $sidebarWidth }) => ({
    position: 'fixed',
    height: '100%',
    width: $sidebarWidth,
    right: $isExtended ? $sidebarWidth : `-${$sidebarWidth}`,
    transition: 'all 0.3s ease-in-out',
    padding: `${pxToRem(16)} ${pxToRem(24)}`,
    flexShrink: 0,
    flexGrow: 1,
    backgroundColor: theme.color.backgroundLightDark,
    boxShadow: $isExtended ? theme.shadow.fancy : 'none',
    clipPath: `inset(0 0 0 ${pxToRem(-32)})`,
    overflow: 'hidden',
    // Prevents shadow glitching during animation on some browsers
    willChange: 'transform',
    transform: 'translateZ(0)',
  })
);

Sidebar.Provider = SidebarProvider;
Sidebar.SimpleHeader = SimpleHeader;
Sidebar.HeaderTitle = HeaderTitle;
Sidebar.Header = SidebarHeader;
Sidebar.Content = SidebarContent;
Sidebar.Footer = SidebarFooter;
Sidebar.Separator = ContentSeparator;
Sidebar.Spinner = SidebarSpinner;
Sidebar.SplitContainer = SidebarSplitContainer;
